import {useEffect, useRef, useState} from 'react';
import {track} from '../services/analytics';

// SMG-4201: how long a player may wait for the next bet before it is worth a report. After a cancel or a settle with
// AutoPlay on, the next bet normally takes 20-50 s (a server job makes it); with AutoPlay off an open bet is
// there within seconds.
const NEXT_BET_WAIT_REPORT_AFTER_MS = {autoPlayOff: 15_000, autoPlayOn: 60_000};

// What the screen knows at the moment of a report. Everything here is a number, a string or a boolean.
type NextBetWaitContext = {
  autoPlayEnabled: boolean, streamBetIdPresent: boolean, streamBetIdCancelled: boolean, sessionBackend: string,
  userSessionState: number | undefined, wagerMode: string, cancelledBetId: string | undefined,
  secSinceCancel: number | undefined,
  // What happened after that cancel: was the cancelled id still in the stream when the cancel was confirmed, how often
  // the stream changed, how many seconds after the cancel its id first went empty, how often the screen looked for bets.
  streamIdAtCancelAck: boolean | undefined, streamChangesSinceCancel: number | undefined,
  secToStreamIdClear: number | undefined, openLooksSinceCancel: number | undefined,
  acceptedLooksSinceCancel: number | undefined,
  oneShotResult: string | undefined,
  // The last answer to a look for an open bet, as the server sent it and as this screen may show it.
  lastOpenRawCount: number | undefined, lastOpenVisibleCount: number | undefined
};

type NextBetEpisodeOptions = {
  // The player is waiting for a bet: signed in, active, focused, no bet on screen.
  active: boolean,
  // A bet is on screen, or on its way there.
  hasBet: boolean,
  autoPlayEnabled: boolean,
  getContext: () => NextBetWaitContext
};

type Episode = { id: string, startedAt: number, timedOutSent: boolean, ended: boolean };

const toSeconds = (ms: number) => Math.round(ms / 1000);

// Every report of the wait goes to Amplitude and also to the browser console in one line, so that whoever reproduces the
// wait (QA, a developer) can copy it from DevTools without access to Amplitude. Properties are numbers, strings and
// booleans only: never a token, a name, or the text of an error.
export const reportNextBetEvent = (name: string, properties: Record<string, unknown>, insertId?: string) => {
  track(name, properties, insertId);
  console.log(`[SMG-4201] ${name}`, JSON.stringify(properties));
};

// One "episode" is one wait for the next bet: it starts when the player has no bet and is able to get one, and it ends
// when a bet appears or the player can no longer get one. It reports a wait that takes long (once) and the bet that
// ends such a wait, so that a player who got stuck can be explained from the analytics alone.
const useFullSwingNextBetEpisode = (options: NextBetEpisodeOptions) => {
  const {active, hasBet} = options;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const episodeRef = useRef<Episode | undefined>();
  // The wait has gone on for as long as it is worth telling the player about (the same moment as the report).
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!active) return;

    const episode: Episode = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, startedAt: Date.now(), timedOutSent: false,
      ended: false
    };
    episodeRef.current = episode;

    const timer = setTimeout(() => {
      if (episode.ended) return;

      episode.timedOutSent = true;
      setStuck(true);
      reportNextBetEvent('Next Bet Wait Timed Out', {
        waitSec: toSeconds(Date.now() - episode.startedAt), ...optionsRef.current.getContext()
      }, `${episode.id}-timed-out`);
    }, optionsRef.current.autoPlayEnabled ? NEXT_BET_WAIT_REPORT_AFTER_MS.autoPlayOn : NEXT_BET_WAIT_REPORT_AFTER_MS.autoPlayOff);

    return () => {
      clearTimeout(timer);
      setStuck(false);
      // A bet that appears ends the episode in the effect below. Any other reason (blur, an inactive player) ends it here.
      if (!optionsRef.current.hasBet) episode.ended = true;
    };
  }, [active]);

  useEffect(() => {
    if (!hasBet) return;

    const episode = episodeRef.current;
    if (!episode || episode.ended) return;

    episode.ended = true;
    // A bet that shows up within the time is the normal case and says nothing. One that came after the report is the
    // end of the wait that was reported.
    if (episode.timedOutSent) {
      reportNextBetEvent('Next Bet Found', {
        waitSec: toSeconds(Date.now() - episode.startedAt), autoPlayEnabled: optionsRef.current.autoPlayEnabled
      }, `${episode.id}-found`);
    }
  }, [hasBet]);

  return {stuck};
};

export default useFullSwingNextBetEpisode;
